import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { ESTADO, MOTIVO_LIBERAR, MOTIVO_PENDIENTE, INCIDENTE_TIPO, TIPO_DOC_OFICINA, faltaNombre } from "@/lib/constants";
import { formatFecha, formatHora, fechaHoraCompleta } from "@/lib/format";
import { desdeCarga, datosDesdeCarga, armarPayload, validarCarga, resumenChoferes, esFlujoExport, cambiarFlujo } from "@/lib/etapas";
import { TipoBadge, EstadoBadge } from "@/components/Badges";
import { ContainerId } from "@/components/marca/ContainerId";
import { RutaCarga } from "@/components/marca/RutaCarga";
import { PageHeader, SectionCard } from "@/components/marca/Layout";
import { DatosCarga } from "@/components/panel/DatosCarga";
import { EtapasEditor } from "@/components/panel/EtapasEditor";
import { DocumentosEtapas } from "@/components/panel/DocumentosEtapas";
import { abrirArchivo } from "@/components/chofer/AgregarArchivo";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Loader2, AlertOctagon, Ban, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

// Fix P (2026-10-13): el detalle de la carga pasa a ser el expediente: datos, etapas editables con lo que reportó el chofer (en verde),
// documentos por etapa, estado operativo, próximo paso y cierre administrativo. Se guarda todo con «Guardar cambios».
const CIERRE = {
  en_curso: { get label() { return t("En curso"); }, cls: "text-ink-2" },
  documentos_pendientes: { get label() { return t("Documents pending"); }, cls: "text-danger font-semibold" },
  cerrada: { get label() { return t("Cerrada"); }, cls: "text-ok font-semibold" },
};

export default function CargaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [carga, setCarga] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [incidentes, setIncidentes] = useState([]);
  const [datos, setDatos] = useState(null);
  const [etapas, setEtapas] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [idExiste, setIdExiste] = useState(false);
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [resolver, setResolver] = useState(null);
  const [accionCarga, setAccionCarga] = useState(null); // 'cancelar' | 'reactivar'

  const cargar = useCallback(async () => {
    const [c, e, inc] = await Promise.all([
      api.get(`/cargas/${id}`),
      api.get(`/cargas/${id}/eventos`),
      api.get(`/incidentes`),
    ]);
    setCarga(c.data);
    setEventos(e.data);
    setIncidentes(inc.data.filter((i) => i.carga_id === id));
  }, [id]);

  useEffect(() => {
    cargar().catch((e) => { toast.error(apiError(e)); navigate("/panel"); });
    api.get("/choferes").then((r) => setChoferes(r.data)).catch(() => {});
    api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
    api.get("/ubicaciones").then((r) => setUbicaciones(r.data)).catch(() => {});
    // Lo que manda el chofer llega solo: se vuelve a leer cada 20 segundos (sin pisar lo que dispatch está editando).
    const tt = setInterval(() => { cargar().catch(() => {}); }, 20000);
    const alCambiarIdioma = () => { cargar().catch(() => {}); };
    window.addEventListener("leonas:idioma", alCambiarIdioma);
    return () => { clearInterval(tt); window.removeEventListener("leonas:idioma", alCambiarIdioma); };
  }, [cargar, navigate]);

  useEffect(() => {
    if (carga && !dirty) {
      setDatos(datosDesdeCarga(carga));
      setEtapas(desdeCarga(carga));
    }
  }, [carga, dirty]);

  const cambiarDato = (campo, valor) => {
    if (campo === "tipo" && valor !== datos.tipo) {
      if (carga.viaje_inicio_at || (carga.etapas || []).some((e) => e.llegada_at || e.salida_at)) {
        toast.error(t("No se puede cambiar el tipo de operación: el viaje ya empezó"));
        return;
      }
      setEtapas((p) => cambiarFlujo(p, valor, carga.fecha || ""));
    }
    setDatos((p) => ({ ...p, [campo]: valor }));
    setDirty(true);
  };
  const cambiarEtapas = (sig) => { setEtapas(sig); setDirty(true); };

  const guardar = async () => {
    if (!dirty) { toast.info(t("No hay cambios para guardar")); return; }
    const problema = validarCarga(datos, etapas, { legacy: !!carga.legacy, idExiste });
    if (problema) { toast.error(problema); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/cargas/${id}`, armarPayload(datos, etapas));
      setDirty(false);
      setCarga(data);
      toast.success(t("Cambios guardados"));
      api.get(`/cargas/${id}/eventos`).then((r) => setEventos(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const abrirDoc = abrirArchivo;

  if (!carga || !datos || !etapas) return <div className="p-6"><Loader2 className="h-6 w-6 animate-spin text-ink-3" /></div>;
  const abiertos = incidentes.filter((i) => i.estado === "abierto");
  const cancelada = carga.estado === "cancelada";
  const op = carga.estado_operativo || {};
  const cierre = CIERRE[carga.cierre_admin] || CIERRE.en_curso;
  const nombres = Object.fromEntries(choferes.map((c) => [c.id, c.nombre]));
  const datosAbiertos = editandoDatos || (!!carga.legacy && !carga.tamano);
  const titulo = carga.load_id || carga.numero_contenedor;
  const mapaUbic = ubicMap(ubicaciones);

  return (
    <div className="p-4 lg:p-8">
      <button onClick={() => navigate("/panel")} className="flex items-center gap-1 text-sm text-ink-3 hover:text-ink mb-4"><ArrowLeft className="h-4 w-4" />{t(" Volver al panel")}</button>

      {cancelada && (
        <div className="bg-[#ECE8E0] rounded-xl border border-line-strong p-4 mb-5 flex flex-wrap items-center justify-between gap-3" data-testid="detalle-cancelada-box">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-bold text-ink-2"><Ban className="h-4 w-4" />{t(" Carga cancelada")}</h3>
            <p className="text-sm text-ink-2">{t("Motivo: {v0}", { v0: carga.cancelada_motivo || "—" })}</p>
            <p className="text-xs text-ink-3 mt-0.5">{t("Por {cancelada_por} · {v1}", { cancelada_por: carga.cancelada_por, v1: fechaHoraCompleta(carga.cancelada_at) })}</p>
          </div>
          <Button data-testid="detalle-reactivar-btn" onClick={() => setAccionCarga("reactivar")}>
            <RotateCcw className="h-4 w-4" />{t(" Reactivar carga")}</Button>
        </div>
      )}

      <PageHeader
        titulo={t("Carga {titulo}", { titulo })}
        subtitulo={`${carga.cliente_nombre || t("Sin cliente")} · ${resumenChoferes(etapas, nombres)}`}
        acciones={<>
          {dirty && <span data-testid="detalle-sin-guardar" className="text-[13px] font-semibold text-[#8A5A0B]">{t("Cambios sin guardar")}</span>}
          {!cancelada && (
            <Button data-testid="detalle-guardar-btn" onClick={guardar} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar cambios")}
            </Button>
          )}
        </>}
      />

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 space-y-5 min-w-0">
          <section className="bg-surface border border-line rounded-xl p-4 lg:p-5" data-testid="detalle-resumen">
            <div className="flex flex-wrap items-center gap-2">
              <TipoBadge tipo={carga.tipo_operacion} />
              <EstadoBadge estado={carga.estado} conProblema={carga.con_problema} />
              {carga.pendiente_reasignar && <span className="text-sm font-semibold text-ink-2">{t("Por reasignar")}</span>}
              {carga.legacy && <span className="text-[12.5px] text-ink-3">{t("Carga anterior al sistema de etapas")}</span>}
            </div>
            <h2 className="font-display font-bold text-[22px] leading-tight text-navy-2 mt-3" data-testid="detalle-titulo">
              {carga.tipo_operacion === "importacion" ? t("Importación") : t("Exportación")} · {carga.tamano || t("tamaño sin definir")} · {op.texto || "—"}
            </h2>
            <p className="mt-2 text-[15px] text-ink flex flex-wrap items-center gap-x-2">
              <span>{t("Cliente: ")}<span className="font-semibold">{carga.cliente_nombre}</span></span>
              <span className="text-ink-3">·</span>
              <span className="flex items-center gap-2">{t("Contenedor asignado: ")}<ContainerId numero={carga.numero_contenedor} size="md" data-testid="detalle-contenedor" /></span>
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-[14px]">
              <Dato label={t("Próximo paso")} valor={op.proximo_paso} testid="detalle-proximo-paso" />
              <Dato label={t("Ubicación actual")} valor={op.ubicacion} testid="detalle-ubicacion" />
              <Dato label={t("Chofer responsable")} valor={carga.chofer_nombre} testid="detalle-chofer" />
              <div>
                <p className="text-ink-3">{t("Cierre administrativo")}</p>
                <p className={cn("mt-0.5", cierre.cls)} data-testid="detalle-cierre">{cierre.label}</p>
                {carga.cierre_admin === "documentos_pendientes" && <p className="text-[12.5px] text-danger">{t("Falta: {v0}", { v0: (carga.docs_faltantes || []).map((d) => d.nombre).join(", ") })}</p>}
              </div>
            </div>
            <div className="mt-5"><RutaCarga carga={carga} /></div>
            {carga.notas && !datosAbiertos && <div className="mt-4 text-sm bg-surface-2 rounded-md p-3 text-ink-2">{carga.notas}</div>}
            {!cancelada && (
              <div className="mt-4">
                {!datosAbiertos && <Button type="button" variant="outline" size="sm" data-testid="detalle-editar-datos" onClick={() => setEditandoDatos(true)}><Pencil className="h-3.5 w-3.5" />{t(" Editar datos de la carga")}</Button>}
                {datosAbiertos && (
                  <div className="pt-4 border-t border-line">
                    {carga.legacy && !carga.tamano && <p className="text-[13.5px] text-[#8A5A0B] mb-3">{t("Esta carga es anterior al sistema de etapas: elige el tamaño del contenedor (y, si quieres, un ID de carga) para poder guardar cambios.")}</p>}
                    <DatosCarga
                      datos={datos}
                      exportNuevo={datos.tipo === "exportacion" && esFlujoExport(etapas)}
                      onChange={cambiarDato}
                      clientes={clientes.filter((c) => c.activo !== false || c.id === datos.cliente_id)}
                      onClienteCreado={(cli) => { setClientes((p) => [cli, ...p]); cambiarDato("cliente_id", cli.id); }}
                      cargaId={id}
                      onIdExiste={setIdExiste}
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          <p className="text-[13px] font-medium text-ok" data-testid="leyenda-verde">{t("Verde = información enviada desde la app del chofer")}</p>

          <EtapasEditor
            etapas={etapas}
            onChange={cambiarEtapas}
            servidor={carga.etapas}
            carga={carga}
            choferes={choferes}
            ubicaciones={ubicaciones}
            abiertasInicial="actual"
            soloLectura={cancelada}
          />

          <DocumentosEtapas carga={carga} onCambio={cargar} abrirDoc={abrirDoc} soloLectura={cancelada} />
        </div>

        <div className="lg:col-span-4 space-y-5">
          {!cancelada && (
            <div className="bg-white rounded-xl border border-line p-4 lg:p-5 space-y-2" data-testid="detalle-acciones-box">
              <h3 className="eyebrow mb-1">{t("Acciones")}</h3>
              <Button data-testid="detalle-cancelar-btn" variant="outline" className="w-full h-10 justify-start text-danger hover:bg-[#FBEDEC] hover:text-danger border-[#E8B9B8]" onClick={() => setAccionCarga("cancelar")}>
                <Ban className="h-4 w-4 mr-2" />{t(" Cancelar carga")}</Button>
            </div>
          )}

          {abiertos.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-[#E8B9B8] p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-danger mb-3"><AlertOctagon className="h-4 w-4" />{t(" Problemas abiertos")}</h3>
              <div className="space-y-2">
                {abiertos.map((i) => (
                  <div key={i.id} data-testid={`detalle-incidente-${i.id}`} className="rounded-lg border border-[#E8B9B8] bg-[#FBEDEC] p-3">
                    <p className="text-sm font-semibold text-danger">{INCIDENTE_TIPO[i.tipo]} · {i.chofer_nombre}</p>
                    {i.descripcion && <p className="text-xs text-ink-2">{i.descripcion}</p>}
                    {i.foto_file_id && <button onClick={() => abrirDoc(i.foto_file_id)} className="text-xs text-ink underline">{t("Ver foto")}</button>}
                    <Button size="sm" data-testid={`detalle-resolver-${i.id}`} className="mt-2 h-9 bg-danger hover:bg-[#A92825] text-white" onClick={() => setResolver(i)}>{t("Resolver")}</Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <SectionCard titulo={t("Línea de tiempo")}>
            <div className="space-y-4" data-testid="detalle-timeline">
              {eventos.map((ev) => (
                <div key={ev.id} className="relative pl-5 border-l-2 border-line">
                  <div className="absolute -left-[5px] top-1 h-2 w-2 rounded-full" style={{ background: dotColor(ev.tipo) }} />
                  <p className="text-sm text-ink">
                    <span className="font-semibold">{ev.usuario_nombre}</span> {describeEvento(ev, mapaUbic)}
                  </p>
                  <p className="text-[12px] text-ink-3 font-mono whitespace-normal">{fechaHoraCompleta(ev.at)}</p>
                  {["editada", "etapa_editada"].includes(ev.tipo) && ev.datos?.cambios && (
                    <ul className="text-xs text-ink-3 mt-1 list-disc list-inside">
                      {Object.entries(ev.datos.cambios).map(([campo, v]) => (
                        <li key={campo}>
                          {CAMPO_LABEL[campo] || campo}
                          {SIN_VALOR.includes(campo) ? ` ${t("cambió")}` : <>: <span className="line-through">{String(v.antes ?? "—")}</span> → {String(v.despues ?? "—")}</>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      {resolver && <ResolverDialog incidente={resolver} onClose={() => setResolver(null)} onDone={() => { setResolver(null); cargar(); }} />}
      {accionCarga === "cancelar" && <CancelarDialog cargaId={id} onClose={() => setAccionCarga(null)} onDone={() => { setAccionCarga(null); setDirty(false); cargar(); }} />}
      {accionCarga === "reactivar" && <ReactivarDialog cargaId={id} onClose={() => setAccionCarga(null)} onDone={() => { setAccionCarga(null); cargar(); }} />}
    </div>
  );
}

const CAMPO_LABEL = {
  get load_id() { return t("ID de carga"); }, get numero_contenedor() { return t("Contenedor"); }, get tipo_operacion() { return t("Tipo"); }, get tamano() { return t("Tamaño"); }, get cliente_id() { return t("Cliente"); }, get notas() { return t("Notas"); },
  get nombre() { return t("Nombre de la etapa"); }, get ubicacion_id() { return t("Ubicación del catálogo"); }, get lugar() { return t("Lugar"); }, get es_yarda_propia() { return t("Yarda propia"); }, get calle() { return t("Calle"); }, get ciudad() { return t("Ciudad"); },
  get estado_us() { return t("State"); }, zip: "ZIP", get fecha() { return t("Fecha"); }, get hora_desde() { return t("Desde"); }, get hora_hasta() { return t("Hasta"); }, get dias() { return t("Días"); }, get referencia() { return t("Referencia"); },
  get instrucciones() { return t("Instrucciones"); }, get zona_horaria() { return t("Zona horaria"); }, get tipo_descarga() { return t("Tipo de descarga"); }, get cita_fecha() { return t("Cita (fecha)"); }, get cita_hora() { return t("Cita (hora)"); },
};
const SIN_VALOR = ["cliente_id", "ubicacion_id"];

function ubicMap(lista) {
  const m = {};
  lista.forEach((u) => { m[u.id] = u.nombre; });
  return m;
}

function dotColor(tipo) {
  if (["cancelada", "incidente_abierto"].includes(tipo)) return "#C4312E";
  if (["reactivada", "incidente_resuelto", "papel_subido", "papel_escaneado", "llegada_registrada", "salida_registrada", "viaje_terminado", "archivo_subido"].includes(tipo)) return "#277A3F";
  if (["estado_cambiado", "siguiente_tramo_programado", "viaje_iniciado", "etapa_editada"].includes(tipo)) return "#16181D";
  return "#D3CDC2";
}

function describeEvento(ev, ubicMap) {
  const d = ev.datos || {};
  const ub = (uid) => ubicMap[uid] || t("una ubicación");
  const et = d.etapa_nombre ? `«${d.etapa_nombre}»` : t("una etapa");
  switch (ev.tipo) {
    case "creada": return t("creó la carga{v0}", { v0: d.load_id ? ` ${d.load_id}` : "" });
    case "editada": return t("editó los datos de la carga");
    case "etapa_editada": return t("editó {et}", { et });
    case "etapa_activada": return t("activó {et}", { et });
    case "etapa_desactivada": return t("quitó {et}", { et });
    case "asignada": return d.chofer_nombre ? t("asignó a {chofer_nombre} a {et}", { chofer_nombre: d.chofer_nombre, et }) : t("asignó un chofer");
    case "reasignada": return d.chofer_nombre ? t("reasignó {et} a {chofer_nombre}", { et, chofer_nombre: d.chofer_nombre }) : t("reasignó la carga a otro chofer");
    case "desasignada": return d.etapa_nombre ? t("quitó el chofer de {et}", { et }) : t("quitó el chofer de la carga");
    case "documento_subido": return t("subió el documento {v0}{v1}", { v0: d.nombre || "", v1: d.tipo && TIPO_DOC_OFICINA[d.tipo] ? ` (${TIPO_DOC_OFICINA[d.tipo]})` : "" }).trim();
    case "documento_tipo_cambiado": return t("cambió el tipo de {v0} a {v1}", { v0: d.nombre || t("un documento"), v1: TIPO_DOC_OFICINA[d.despues] || d.despues });
    case "archivo_subido": return t("subió {v0} en {et}{v2}", { v0: d.nombre || t("un archivo"), et, v2: d.origen === "oficina" ? t(" (desde la oficina)") : "" });
    case "viaje_iniciado": return t("empezó el viaje");
    case "llegada_registrada": return t("registró su llegada a {et}", { et });
    case "salida_registrada": return t("salió de {et}", { et });
    case "viaje_terminado": return t("terminó el viaje");
    case "etapa_registro": return t("registró {v0}", { v0: d.campo === "espacio" ? t("el espacio {v0}", { v0: d.valor || "" }) : d.campo === "descarga_terminada" ? t("la descarga terminada") : d.campo === "vacio_devuelto" ? t("el vacío devuelto") : d.campo === "chassis_devuelto" ? t("el chassis devuelto") : t("un dato") }).trim();
    case "chasis_registrado": return t("registró el chasis {v0}", { v0: d.chasis_numero || "" }).trim();
    case "contenedor_registrado":
      return d.coincide ? t("registró el contenedor {contenedor}", { contenedor: d.contenedor }) : t("registró el contenedor {contenedor} (no coincide, esperado {esperado})", { contenedor: d.contenedor, esperado: d.esperado });
    case "estado_cambiado":
      return t("cambió el estado de {v0} a {v1}{v2}", { v0: ESTADO[d.de]?.label || d.de, v1: ESTADO[d.a]?.label || d.a, v2: d.ubicacion_id ? t(" — quedó en {ub}", { ub: ub(d.ubicacion_id) }) : "" });
    case "eta_actualizada": return t("cargó la ETA del delivery: {v0}", { v0: d.eta_fecha ? `${formatFecha(d.eta_fecha)} ${d.eta_hora || ""}`.trim() : formatHora(d.eta) });
    case "papel_subido": return t("subió un papel firmado");
    case "papel_escaneado": return t("subió el Signed DO / POD{v0}", { v0: d.legibilidad ? t(" (legibilidad {v0} de 5)", { v0: d.legibilidad }) : "" });
    case "papel_subido_con_observaciones": return t("subió el papel con observaciones{v0}", { v0: d.motivo ? t(" — motivo: {v0}", { v0: d.motivo }) : "" });
    case "cancelada": return t("canceló la carga{v0}", { v0: d.motivo ? t(" — motivo: {v0}", { v0: d.motivo }) : "" });
    case "reactivada": return t("reactivó la carga");
    case "siguiente_tramo_programado":
      return t("programó el siguiente tramo con {v0}{v1}{v2}", { v0: d.chofer_nuevo_nombre || t("otro chofer"), v1: d.fecha ? t(" para el {formatFecha}", { formatFecha: formatFecha(d.fecha) }) : "", v2: d.nota ? ` — ${d.nota}` : "" });
    case "incidente_abierto": return t("reportó un problema: {v0}", { v0: INCIDENTE_TIPO[d.tipo] || d.tipo });
    case "incidente_resuelto": return t("resolvió el problema{v0}", { v0: d.nota ? ` — ${d.nota}` : "" });
    case "liberada":
      return t("liberó sus etapas pendientes — motivo: {v0}{v1}{v2}", { v0: MOTIVO_LIBERAR[d.motivo] || d.motivo, v1: d.texto ? ` (${d.texto})` : "", v2: d.ubicacion_id ? t(" — quedó en {ub}", { ub: ub(d.ubicacion_id) }) : "" });
    case "pendiente_informado":
      return t("informó un pendiente: falta {v0} — {v1}{v2}", { v0: faltaNombre(d.falta), v1: MOTIVO_PENDIENTE[d.motivo] || d.motivo, v2: d.texto ? ` (${d.texto})` : "" });
    default: return ev.tipo;
  }
}

function Dato({ label, valor, testid }) {
  return (
    <div>
      <p className="text-ink-3">{label}</p>
      <p className="font-semibold text-ink mt-0.5" data-testid={testid}>{valor || "—"}</p>
    </div>
  );
}

function ResolverDialog({ incidente, onClose, onDone }) {
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    setSaving(true);
    try {
      await api.post(`/incidentes/${incidente.id}/resolver`, { nota });
      toast.success(t("Incidente resuelto"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{t("Resolver incidente — {v0}", { v0: INCIDENTE_TIPO[incidente.tipo] })}</DialogTitle></DialogHeader>
        <Textarea data-testid="detalle-resolver-nota" placeholder={t("Nota de resolución (opcional)")} value={nota} onChange={(e) => setNota(e.target.value)} rows={3} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button data-testid="detalle-resolver-guardar" className="" onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Marcar resuelto")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelarDialog({ cargaId, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const confirmar = async () => {
    if (!motivo.trim()) return toast.error(t("El motivo es obligatorio"));
    setSaving(true);
    try {
      await api.post(`/cargas/${cargaId}/cancelar`, { motivo: motivo.trim() });
      toast.success(t("Carga cancelada"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="text-danger">{t("Cancelar carga")}</DialogTitle></DialogHeader>
        <Textarea data-testid="cancelar-motivo" placeholder={t("Motivo de la cancelación")} value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("Volver")}</Button>
          <Button data-testid="cancelar-confirmar" className="bg-danger hover:bg-[#A92825] text-white" onClick={confirmar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Cancelar carga")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReactivarDialog({ cargaId, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const confirmar = async () => {
    setSaving(true);
    try {
      await api.post(`/cargas/${cargaId}/reactivar`);
      toast.success(t("Carga reactivada"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{t("Reactivar carga")}</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-2">{t("La carga vuelve a su estado anterior a la cancelación, con el avance y los choferes que tenía en cada etapa.")}</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("Volver")}</Button>
          <Button data-testid="reactivar-confirmar" className="" onClick={confirmar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Reactivar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
